import { default as React, useEffect, useMemo, useRef, useState } from "react";
import { useThemeSwitcher } from "react-css-theme-switcher";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import _ from "lodash";
import { icon, label, number, quote } from "../../../config";
import { filterData } from "../../../utils/kendo";
import { DrutasDropdownFlowElements } from "../../DrutasDropdown/DrutasDropdownFlowElements";
import { getFilterAssigneeList, getProjectList } from "../../TaskSidebar/sidebar.helper";
import { handleWorkflowAssignment, getUserFilter, getProjectFilter, getHybridFilter, isUserItem, getPrivacyClass } from "../workflowHelper";

const editField = "inEdit";

/**
 * In the flow elements this dropdown is used to set AssignedId
 * Parent - DetailedFlowElementDetails
 * @author Sarthak Arora, Muskan Thakur
 */
const FlowElementsAssignedId = (props) => {
  const [assignedIdList, setAssignedIdList] = useState([]);
  const [assignedIdListFilter, setAssignedIdListFilter] = useState(null);
  const [filterText, setFilterText] = useState("");
  const { projectId } = useParams();
  const { currentTheme } = useThemeSwitcher();
  const { task, newTaskData } = useSelector((state) => state.taskSidebar);
  const { user } = useSelector((state) => state.auth);
  const workflowOrTaskProjectId = projectId ? projectId : task.CurrentProject ? task.CurrentProject : newTaskData.project;
  const { defaultDetails } = useSelector((state) => state.tasks);

  const {
    flowTaskAssignmentId,
    setFlowTaskAssignmentId,
    flowAssignmentType,
    setFlowAssignmentType,
    setFlowWorkflowAssignmentType,
    setFlowWorkflowAssignmentId,
    workflowAssignmentTypes,
    selection,
    setSelection,
    taskAssignmentTypes,
    flowElementData,
  } = props;

  const inputRef = useRef(null);
  const dynamicPlaceholder = useMemo(() => {
    if (flowAssignmentType?.value === number.TWO) return quote.SEARCH_USER;
    if (flowAssignmentType?.value === number.THREE) return quote.SEARCH_TEAM;
    if (selection?.length === number.TWO) return quote.SEARCH_TEAM_USER;
  }, [flowAssignmentType?.value, selection]);

  /**
   * Initialize flowAssignmentType and selection
   * @author Sarthak Arora, Muskan Thakur
   */
  useEffect(() => {
    if (!flowAssignmentType) {
      const requiredAssignType = taskAssignmentTypes?.find((c) => c.value === props.dataItem?.taskAssignmentType);
      setFlowAssignmentType(requiredAssignType || taskAssignmentTypes?.[number.ZERO] || null);
    }
    if (!selection || selection.length === 0) {
      setSelection([label.TEAM_SELECTION, label.USER_SELECTION]);
    }
  }, [props.dataItem?.taskAssignmentType, taskAssignmentTypes, flowElementData, flowAssignmentType, selection, setFlowAssignmentType, setSelection]);

  /**
   * Set flow assignment Id for new and existing flow element
   * @author Sarthak Arora, Muskan Thakur
   */
  useEffect(() => {
    switch (props?.dataItem?.taskAssignmentType) {
      case number.TWO: {
        const assigneeList = defaultDetails?.assigneeList || [];
        const targetId = props?.dataItem?.taskAssignmentId;
        const assignee = targetId
          ? assigneeList.find((c) => c.value === targetId)
          : assigneeList.find((c) => c.value === assignedIdList[number.ZERO]?.value);
        if (assignee) assignee.AssignmentType = number.TWO;
        setFlowTaskAssignmentId(assignee || null);
        break;
      }
      case number.THREE: {
        const allProjectList = _.orderBy([...defaultDetails?.allProjectsList], [(p) => p?.label?.toLowerCase()?.trim()], ["asc"]);
        const targetId = props?.dataItem?.taskAssignmentId;
        const project = targetId
          ? allProjectList.find((c) => c?.value === targetId)
          : allProjectList.find((c) => c?.value === assignedIdList[number.ZERO]?.ProjectId);
        if (project) project.AssignmentType = number.THREE;
        setFlowTaskAssignmentId(project || null);
        break;
    }
    }
  }, [props.dataItem?.taskAssignmentId, props.dataItem?.taskAssignmentType, assignedIdList, defaultDetails, setFlowTaskAssignmentId]);

  /**
   * Auto-select logged-in user when only Users is selected for new flow elements.
   * Only for new flow elements. If only users selected and no assignee is currently selected,
   * auto-select logged-in user. Priority 1: Try to find logged-in user in the list.
   * Fallback: If logged-in user not in list, select first user.
   * @author Muskan Thakur
   */
  useEffect(() => {
    if (!props?.dataItem?.id && props?.dataItem?.[editField]) {
      const isOnlyUsers = selection.length === number.ONE && selection?.includes(label.USER_SELECTION);
      
      if (isOnlyUsers && !flowTaskAssignmentId && assignedIdList?.length > 0 && user?.id) {
        const loggedInUser = assignedIdList.find((userItem) => userItem.value === user.id && userItem.AssignmentType === number.TWO);
        if (loggedInUser) {
          setFlowTaskAssignmentId(loggedInUser);
        } else {
          const firstUser = assignedIdList[number.ZERO];
          if (firstUser && firstUser.AssignmentType === number.TWO) {
            setFlowTaskAssignmentId(firstUser);
          }
        }
      }
    }
  }, [assignedIdList, selection, flowTaskAssignmentId, user?.id, props?.dataItem?.id, props?.dataItem?.[editField], setFlowTaskAssignmentId]);

  /**
   * Handle workflow assignment type for new flow elements when user is selected.
   * Only for new flow elements when user is selected.
   * @author Sarthak Arora, Muskan Thakur
   */
  useEffect(() => {
    if (!props?.dataItem?.id && flowAssignmentType?.value === number.TWO) {
      setFlowWorkflowAssignmentType(workflowAssignmentTypes?.find((c) => c.value === number.FIVE));
    }
  }, [flowAssignmentType, workflowAssignmentTypes, setFlowWorkflowAssignmentType]);

  /**
   * Load assignedId list when dependencies change
   * @author Sarthak Arora, Muskan Thakur
   */
  useEffect(() => {
    getAndFilterAssigneIdList();
  }, [flowAssignmentType, props?.dataItem?.taskAssignmentType, defaultDetails, selection, workflowOrTaskProjectId]);

  /**
   * Helper: Process and set list data
   * @author Muskan Thakur
   */
  const setListData = (list, filter, searchValue) => {
    const filtered = searchValue ? filterData(filter, list) : list;
    if (searchValue) {
      setAssignedIdListFilter(filtered);
    } else {
      setAssignedIdList(filtered);
      clearFilterText();
    }
  };

  /**
   * Main function to get and filter assignee list.
   * Handles three scenarios: Both Teams and Users selected, Only Users selected, Only Teams selected.
   * Falls back to flowAssignmentType if none of the above conditions match.
   * @author Sarthak Arora, Muskan Thakur
   */
  const getAndFilterAssigneIdList = (event) => {
    const searchValue = event?.target?.value || "";
    setFilterText(searchValue);

    const isBothSelected = selection.length === 2;
    const isOnlyUsers = selection.length === 1 && selection?.includes(label.USER_SELECTION);
    const isOnlyTeams = selection.length === 1 && selection?.includes(label.TEAM_SELECTION);

    if (isBothSelected) {
      const assignees = workflowOrTaskProjectId ? getFilterAssigneeList(null, workflowOrTaskProjectId, true) : [];
      const teams = getProjectList(parseInt(workflowOrTaskProjectId), true);
      const combined = [
        ...teams.map((p) => ({ ...p, AssignmentType: number.THREE })),
        ...assignees.map((a) => ({ ...a, AssignmentType: number.TWO })),
      ];
      setListData(combined, searchValue ? getHybridFilter(searchValue) : null, searchValue);
      return;
    }

    if (isOnlyUsers) {
      const assignees = workflowOrTaskProjectId ? getFilterAssigneeList(null, workflowOrTaskProjectId, true) : [];
      const list = assignees.map((a) => ({ ...a, AssignmentType: number.TWO }));
      setListData(list, searchValue ? getUserFilter(searchValue) : null, searchValue);
      return;
    }

    if (isOnlyTeams) {
      const projects = getProjectList(parseInt(workflowOrTaskProjectId), true);
      const list = projects.map((p) => ({ ...p, AssignmentType: number.THREE }));
      setListData(list, searchValue ? getProjectFilter(searchValue) : null, searchValue);
      return;
    }

    switch (flowAssignmentType?.value) {
      case number.TWO: {
        const assignees = workflowOrTaskProjectId ? getFilterAssigneeList(null, workflowOrTaskProjectId, true) : [];
        const list = assignees.map((a) => ({ ...a, AssignmentType: number.TWO }));
        setListData(list, searchValue ? getUserFilter(searchValue) : null, searchValue);
        break;
      }
      case number.THREE: {
        const projects = getProjectList(parseInt(workflowOrTaskProjectId), true);
        const list = projects.map((p) => ({ ...p, AssignmentType: number.THREE }));
        setListData(list, searchValue ? getProjectFilter(searchValue) : null, searchValue);
        break;
      }
    }
  };

  /**
   * Handle change in selected assignee
   * @author Sarthak Arora, Muskan Thakur
   */
  const handleChange = async (event) => {
    if (!event) return;
      const selectedAssignId = event;
      setFlowTaskAssignmentId(selectedAssignId);
    if (selectedAssignId?.AssignmentType === number.TWO) {
        setFlowWorkflowAssignmentType(workflowAssignmentTypes?.find((c) => c.value === number.FIVE));
    } else {
      handleWorkflowAssignment(selectedAssignId, selectedAssignId?.AssignmentType, setFlowWorkflowAssignmentType, setFlowWorkflowAssignmentId);
    }
  };

  /**
   * Handle filter button click (Teams/Users toggle)
   * @author Sarthak Arora, Muskan Thakur
   */
  const handleFilter = (filterType) => {
    const isUserFilter = filterType === label.USER_SELECTION || filterType === "user";
    const isTeamFilter = filterType === label.TEAM_SELECTION || filterType === "team";
    const hasBoth = selection.length === 2;
    const isIncluded = selection?.includes(filterType);

    if (isUserFilter) {
      if (isIncluded && hasBoth) {
        setFlowAssignmentType(taskAssignmentTypes?.find((type) => type.value === number.THREE));
        setSelection(selection.filter((item) => item !== filterType));
      } else if (!isIncluded) {
        const newSelections = [...selection, filterType];
        if (newSelections.length === 2) {
          setFlowAssignmentType(taskAssignmentTypes?.find((type) => type.value === number.THREE));
        }
        setSelection(newSelections);
      }
    } else if (isTeamFilter) {
      if (isIncluded && hasBoth) {
        setFlowAssignmentType(taskAssignmentTypes?.find((type) => type.value === number.TWO));
        setSelection(selection.filter((item) => item !== filterType));
      } else if (!isIncluded) {
        const newSelections = [...selection, filterType];
        if (newSelections.length === 2) {
          setFlowAssignmentType(taskAssignmentTypes?.find((type) => type.value === number.THREE));
        }
        setSelection(newSelections);
      }
    }
  };

  const focusInputRef = (element) => {
    inputRef.current = element;
    if (element) setTimeout(() => element.focus(), 10);
  };

  const filtersContent = () => (
        <div className='p-1 mt-1 pb-2'>
          <div className='d-flex align-items-center justify-content-between'>
            <span>Search with</span>
            <div className='toggle-switch-btn'>
              <button className={`switch-btn min-width-75 toggle-switch-btn-padding ${selection?.includes("team") ? "active" : "de-activate"}`} onClick={() => handleFilter("team")}>
                {icon.GROUP} Teams
              </button>
              <button className={`switch-btn min-width-75 toggle-switch-btn-padding ${selection?.includes("user") ? "active" : "de-activate"}`} onClick={() => handleFilter("user")}>
                {icon.TASK_PERSON} Users
              </button>
            </div>
          </div>
          <div className='assignto-search mt-3 position-relative'>
            <span className='assignto-search-icon'>{icon.SEARCH}</span>
            <input
              ref={focusInputRef}
              autoFocus={true}
              className='assignto-search-field form-control w-100'
              placeholder={dynamicPlaceholder}
              value={filterText}
              onChange={getAndFilterAssigneIdList}
            />
          </div>
        </div>
    );

  /**
   * Render dropdown item
   * @author Muskan Thakur
   */
  const itemRenderNew = (item, handleOnClick, handleElementMouseDown) => {
    const isUser = isUserItem(item, flowAssignmentType);
    return (
          <div
            className='d-flex align-items-center drutas-dropdown-popup-element px-3 py-2 truncate-text'
            onClick={() => handleOnClick(item)}
            onMouseDown={handleElementMouseDown}>
        <div className={`profile-picture-container mr-3 ${!isUser ? getPrivacyClass(item?.Privacy) : ""}`}>
          {isUser ? (
            item?.PhotoLinks?.small ? (
              <img src={item?.PhotoLinks?.small} className='profile-picture' />
            ) : (
              <span>{icon.USER_ICON}</span>
            )
          ) : (
            icon.PROJECT_ICON
          )}
            </div>
            <div className='truncate-text d-flex align-items-center'>
              <div className='truncate-text'>
                <div className='assignee-name truncate-text'>{item?.label}</div>
            <div className='assignee-email truncate-text'>{isUser ? item?.Email : item?.ProjectCode}</div>
              </div>
            </div>
          </div>
    );
  };

  /**
   * Render dropdown value.
   * Handles both unassigned state (when no item is selected) and assigned state (when an item is selected).
   * @author Sarthak Arora, Muskan Thakur
   */
  const valueRenderNew = (item) => {
    if (!item) {
      const onlyUsersSelected = selection?.length === 1 && (selection?.includes("user") || selection?.includes(label.USER_SELECTION));
      const onlyTeamsSelected = selection?.length === 1 && (selection?.includes("team") || selection?.includes(label.TEAM_SELECTION));
      const isUserMode = onlyUsersSelected || flowAssignmentType?.value === number.TWO;
      const showUserIcon = isUserMode || (!onlyTeamsSelected && flowAssignmentType?.value !== number.THREE);

      return (
        <div className='d-flex align-items-center justify-content-between drutas-dropdown-popup-element truncate-text'>
          <div className='d-flex align-items-center truncate-text'>
            <div className={`image-box mr-3 ${!showUserIcon ? "black-icon" : ""}`}>
              {showUserIcon ? <span className='assignee-icon'>{icon.USER_ICON}</span> : icon.PROJECT_ICON}
            </div>
            <div className='truncate-text'>
              <div className='assignee-name truncate-text'>{label.UNASSIGNED}</div>
            </div>
          </div>
          <span>{icon.ARROW_DROP_DOWN_ICON}</span>
        </div>
      );
        }

    const isUser = isUserItem(item, flowAssignmentType);
    return (
          <div className='d-flex align-items-center justify-content-between drutas-dropdown-popup-element truncate-text'>
        <div className='d-flex align-items-center truncate-text'>
          <div className={`image-box mr-3 ${!isUser ? getPrivacyClass(item?.Privacy) : ""}`}>
            {isUser ? (
              item?.PhotoLinks?.small ? (
                <img src={item?.PhotoLinks?.small} alt={item?.label} />
              ) : (
                <span className='assignee-icon'>{icon.USER_ICON}</span>
              )
            ) : (
              icon.PROJECT_ICON
            )}
              </div>
              <div className='truncate-text'>
                <div className='assignee-name truncate-text'>{item.label}</div>
              </div>
            </div>
            <span>{icon.ARROW_DROP_DOWN_ICON}</span>
          </div>
    );
  };

  /**
   * Clear filter text
   * @author Muskan Thakur
   */
  const clearFilterText = () => {
    setAssignedIdListFilter(null);
  };

  /** Set title to Unassigned when no assignee is selected */
  const tooltipTitle = flowTaskAssignmentId?.label || label.UNASSIGNED;

  return (
    <span title={tooltipTitle}>
        <DrutasDropdownFlowElements
          value={flowTaskAssignmentId}
          onChange={handleChange}
          data={assignedIdListFilter || assignedIdList}
        label='label'
        key='value'
          isfilter={true}
          filterContent={filtersContent}
          itemRender={itemRenderNew}
          valueRender={valueRenderNew}
          disabled={!props?.dataItem?.[editField]}
          popupClass=''
          clearFilterText={clearFilterText}
        />
      </span>
  );
};

export default FlowElementsAssignedId;
